import {
  parseLoomAuthoringCommand,
  parseLoomAuthoringDocument,
  reduceLoomAuthoringCommand,
  type LoomAuthoringCommand,
  type LoomAuthoringDocument,
  type LoomSceneNode,
} from "./loomAuthoringModel";

export interface LoomAuthoringDefinition {
  schema: "loom-authoring/v1";
  documentId: string;
  nodes: readonly LoomSceneNode[];
  rootNodeIds: readonly string[];
  presetBinding?: LoomAuthoringDocument["presetBinding"];
}

export interface LoomAuthoringStateRecord {
  schema: "loom-authoring-state/v1";
  stateId: string;
  definition: LoomAuthoringDefinition;
}

export interface LoomAuthoringHead {
  stateId: string;
  revision: number;
}

interface LoomOperationBase {
  schema: "loom-authoring-operation/v1";
  operationId: string;
  sequence: number;
}

export interface LoomCommandAcceptedOperation extends LoomOperationBase {
  kind: "command.accepted";
  origin: LoomAuthoringOperationOrigin;
  subjectIds: readonly string[];
  extraction: "rebuild-required" | "unchanged";
  parent: LoomAuthoringHead;
  result: LoomAuthoringHead;
  command: LoomAuthoringCommand;
  discardedRedoOperationIds: readonly string[];
}

export interface LoomHeadMovedOperation extends LoomOperationBase {
  kind: "head.moved";
  move: "undo" | "redo";
  from: LoomAuthoringHead;
  to: LoomAuthoringHead;
  transitionOperationId: string;
}

export type LoomAuthoringOperation = LoomCommandAcceptedOperation | LoomHeadMovedOperation;

export type LoomAuthoringOperationOrigin =
  | "api.command"
  | "assistant.ollama"
  | "gui.control"
  | "gui.scene"
  | "system.migration";

export interface LoomCommandAcceptance {
  origin: LoomAuthoringOperationOrigin;
  subjectIds?: readonly string[];
}

export interface LoomAuthoringHistory {
  schema: "loom-authoring-history/v1";
  initialHead: LoomAuthoringHead;
  states: readonly LoomAuthoringStateRecord[];
  operations: readonly LoomAuthoringOperation[];
  activeBranchOperationIds: readonly string[];
  cursor: number;
  activeHead: LoomAuthoringHead;
}

export interface LoomDependentAncestry {
  stateIds: readonly string[];
  commandOperationIds: readonly string[];
}

type UnknownRecord = Record<string, unknown>;
let fallbackCommandSequence = 0;

export function createLoomCommandId(prefix: string, expectedRevision: number): string {
  const safePrefix = prefix.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
  if (!safePrefix) throw new Error("command id prefix is empty");
  const uuid = globalThis.crypto?.randomUUID?.();
  fallbackCommandSequence += 1;
  return `${safePrefix}-r${expectedRevision}-${uuid ?? `${Date.now()}-${fallbackCommandSequence}`}`;
}

function record(value: unknown, path: string): UnknownRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error(`${path} must be an object`);
  return value as UnknownRecord;
}

function exactKeys(value: UnknownRecord, allowed: readonly string[], path: string): void {
  const unknown = Object.keys(value).filter((key) => !allowed.includes(key));
  if (unknown.length > 0) throw new Error(`${path} contains unknown fields: ${unknown.join(", ")}`);
}

function integer(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) throw new Error(`${path} must be a nonnegative integer`);
  return value;
}

function nonEmpty(value: unknown, path: string): string {
  if (typeof value !== "string" || value.trim() === "") throw new Error(`${path} must be a non-empty string`);
  return value;
}

function uniqueStrings(value: unknown, path: string): string[] {
  if (!Array.isArray(value)) throw new Error(`${path} must be an array`);
  const items = value.map((item, index) => nonEmpty(item, `${path}[${index}]`));
  if (new Set(items).size !== items.length) throw new Error(`${path} contains duplicate ids`);
  return items;
}

function canonicalJson(value: unknown): string {
  if (value === null || typeof value === "boolean" || typeof value === "string") return JSON.stringify(value);
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error("canonical content contains a non-finite number");
    return Object.is(value, -0) ? "0" : JSON.stringify(value);
  }
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (typeof value === "object") {
    const item = value as UnknownRecord;
    const keys = Object.keys(item).sort();
    if (keys.some((key) => item[key] === undefined)) throw new Error("canonical content contains undefined");
    return `{${keys.map((key) => `${JSON.stringify(key)}:${canonicalJson(item[key])}`).join(",")}}`;
  }
  throw new Error(`canonical content contains unsupported ${typeof value}`);
}

function contentId(prefix: string, value: unknown): string {
  let hash = 0xcbf29ce484222325n;
  for (const byte of new TextEncoder().encode(canonicalJson(value))) {
    hash = BigInt.asUintN(64, (hash ^ BigInt(byte)) * 0x100000001b3n);
  }
  return `${prefix}-${hash.toString(16).padStart(16, "0")}`;
}

function immutable<T>(value: T): T {
  if (typeof value !== "object" || value === null || Object.isFrozen(value)) return value;
  Object.values(value as UnknownRecord).forEach(immutable);
  return Object.freeze(value);
}

function definitionFromDocument(document: LoomAuthoringDocument): LoomAuthoringDefinition {
  return immutable({
    schema: "loom-authoring/v1",
    documentId: document.documentId,
    nodes: document.nodes,
    rootNodeIds: document.rootNodeIds,
    ...(document.presetBinding ? { presetBinding: document.presetBinding } : {}),
  });
}

function parseDefinition(value: unknown, path: string): LoomAuthoringDefinition {
  const item = record(value, path);
  exactKeys(item, ["schema", "documentId", "nodes", "rootNodeIds", "presetBinding"], path);
  return definitionFromDocument(parseLoomAuthoringDocument({ ...item, revision: 0 }));
}

function stateRecord(document: LoomAuthoringDocument): LoomAuthoringStateRecord {
  const definition = definitionFromDocument(document);
  return immutable({ schema: "loom-authoring-state/v1", stateId: contentId("loom-state", definition), definition });
}

function parseState(value: unknown, path: string): LoomAuthoringStateRecord {
  const item = record(value, path);
  exactKeys(item, ["schema", "stateId", "definition"], path);
  if (item.schema !== "loom-authoring-state/v1") throw new Error(`${path}.schema must be loom-authoring-state/v1`);
  const definition = parseDefinition(item.definition, `${path}.definition`);
  const expectedId = contentId("loom-state", definition);
  if (item.stateId !== expectedId) throw new Error(`${path}.stateId does not match its content`);
  return immutable({ schema: "loom-authoring-state/v1", stateId: expectedId, definition });
}

function parseHead(value: unknown, path: string): LoomAuthoringHead {
  const item = record(value, path);
  exactKeys(item, ["stateId", "revision"], path);
  return immutable({ stateId: nonEmpty(item.stateId, `${path}.stateId`), revision: integer(item.revision, `${path}.revision`) });
}

function operationContent(operation: LoomAuthoringOperation): unknown {
  const base = { schema: operation.schema, sequence: operation.sequence, kind: operation.kind };
  return operation.kind === "command.accepted"
    ? { ...base, origin: operation.origin, subjectIds: operation.subjectIds, extraction: operation.extraction, parent: operation.parent, result: operation.result, command: operation.command, discardedRedoOperationIds: operation.discardedRedoOperationIds }
    : { ...base, move: operation.move, from: operation.from, to: operation.to, transitionOperationId: operation.transitionOperationId };
}

function sealOperation<T extends LoomAuthoringOperation>(operation: T): T {
  return immutable({ ...operation, operationId: contentId("loom-operation", operationContent(operation)) });
}

function parseOperation(value: unknown, path: string): LoomAuthoringOperation {
  const item = record(value, path);
  const baseKeys = ["schema", "operationId", "sequence", "kind"];
  if (item.schema !== "loom-authoring-operation/v1") throw new Error(`${path}.schema must be loom-authoring-operation/v1`);
  const base = { schema: "loom-authoring-operation/v1" as const, operationId: nonEmpty(item.operationId, `${path}.operationId`), sequence: integer(item.sequence, `${path}.sequence`) };
  let operation: LoomAuthoringOperation;
  if (item.kind === "command.accepted") {
    exactKeys(item, [...baseKeys, "origin", "subjectIds", "extraction", "parent", "result", "command", "discardedRedoOperationIds"], path);
    if (!(["api.command", "assistant.ollama", "gui.control", "gui.scene", "system.migration"] as const).includes(item.origin as LoomAuthoringOperationOrigin)) throw new Error(`${path}.origin is unsupported`);
    if (item.extraction !== "rebuild-required" && item.extraction !== "unchanged") throw new Error(`${path}.extraction is unsupported`);
    operation = {
      ...base,
      kind: "command.accepted",
      origin: item.origin as LoomAuthoringOperationOrigin,
      subjectIds: uniqueStrings(item.subjectIds, `${path}.subjectIds`),
      extraction: item.extraction,
      parent: parseHead(item.parent, `${path}.parent`),
      result: parseHead(item.result, `${path}.result`),
      command: parseLoomAuthoringCommand(item.command),
      discardedRedoOperationIds: uniqueStrings(item.discardedRedoOperationIds, `${path}.discardedRedoOperationIds`),
    };
  } else if (item.kind === "head.moved") {
    exactKeys(item, [...baseKeys, "move", "from", "to", "transitionOperationId"], path);
    if (item.move !== "undo" && item.move !== "redo") throw new Error(`${path}.move is unsupported`);
    operation = {
      ...base,
      kind: "head.moved",
      move: item.move,
      from: parseHead(item.from, `${path}.from`),
      to: parseHead(item.to, `${path}.to`),
      transitionOperationId: nonEmpty(item.transitionOperationId, `${path}.transitionOperationId`),
    };
  } else throw new Error(`${path}.kind is unsupported`);
  if (operation.operationId !== contentId("loom-operation", operationContent(operation))) throw new Error(`${path}.operationId does not match its content`);
  return immutable(operation);
}

function sameHead(left: LoomAuthoringHead, right: LoomAuthoringHead): boolean {
  return left.stateId === right.stateId && left.revision === right.revision;
}

function documentAt(states: ReadonlyMap<string, LoomAuthoringStateRecord>, head: LoomAuthoringHead): LoomAuthoringDocument {
  const state = states.get(head.stateId);
  if (!state) throw new Error(`head references missing state ${head.stateId}`);
  return parseLoomAuthoringDocument({ ...state.definition, revision: head.revision });
}

function validateReplay(history: LoomAuthoringHistory): void {
  const states = new Map(history.states.map((state) => [state.stateId, state]));
  if (states.size !== history.states.length) throw new Error("history.states contains duplicate ids");
  documentAt(states, history.initialHead);
  const byOperationId = new Map<string, LoomCommandAcceptedOperation>();
  const operationIds = new Set<string>();
  const commandIds = new Set<string>();
  let branch: string[] = [];
  let cursor = 0;
  let head = history.initialHead;
  history.operations.forEach((operation, sequence) => {
    if (operation.sequence !== sequence) throw new Error("history.operations sequence is not contiguous");
    if (operationIds.has(operation.operationId)) throw new Error(`duplicate operation id ${operation.operationId}`);
    operationIds.add(operation.operationId);
    if (operation.kind === "command.accepted") {
      if (commandIds.has(operation.command.commandId)) throw new Error(`duplicate command id ${operation.command.commandId}`);
      commandIds.add(operation.command.commandId);
      if (!sameHead(operation.parent, head)) throw new Error(`operation ${operation.operationId} does not start at the active head`);
      if (operation.command.expectedRevision !== operation.parent.revision) throw new Error(`operation ${operation.operationId} has a stale command revision`);
      const reduction = reduceLoomAuthoringCommand(documentAt(states, operation.parent), operation.command);
      const result = reduction.document;
      if (reduction.extraction !== operation.extraction) throw new Error(`operation ${operation.operationId} has invalid extraction metadata`);
      const resultState = stateRecord(result);
      if (resultState.stateId !== operation.result.stateId || result.revision !== operation.result.revision) throw new Error(`operation ${operation.operationId} result is invalid`);
      const storedResult = states.get(operation.result.stateId);
      if (!storedResult || canonicalJson(storedResult.definition) !== canonicalJson(resultState.definition)) throw new Error(`operation ${operation.operationId} result content is invalid`);
      documentAt(states, operation.result);
      const discarded = branch.slice(cursor);
      if (canonicalJson(discarded) !== canonicalJson(operation.discardedRedoOperationIds)) throw new Error(`operation ${operation.operationId} has invalid branch metadata`);
      branch = [...branch.slice(0, cursor), operation.operationId];
      cursor += 1;
      head = operation.result;
      byOperationId.set(operation.operationId, operation);
    } else {
      if (!sameHead(operation.from, head)) throw new Error(`operation ${operation.operationId} does not move the active head`);
      const transitionId = operation.move === "undo" ? branch[cursor - 1] : branch[cursor];
      if (transitionId === undefined || transitionId !== operation.transitionOperationId) throw new Error(`operation ${operation.operationId} has invalid ${operation.move} target`);
      const transition = byOperationId.get(transitionId);
      if (!transition) throw new Error(`operation ${operation.operationId} references a missing transition`);
      const target = operation.move === "undo" ? transition.parent : transition.result;
      if (!sameHead(operation.to, target)) throw new Error(`operation ${operation.operationId} moves to the wrong head`);
      cursor += operation.move === "undo" ? -1 : 1;
      head = target;
    }
  });
  if (canonicalJson(branch) !== canonicalJson(history.activeBranchOperationIds)) throw new Error("history.activeBranchOperationIds is invalid");
  if (cursor !== history.cursor || !sameHead(head, history.activeHead)) throw new Error("history active cursor/head is invalid");
  const referencedStates = new Set([history.initialHead.stateId]);
  history.operations.forEach((operation) => {
    if (operation.kind === "command.accepted") referencedStates.add(operation.result.stateId);
  });
  if (referencedStates.size !== states.size || history.states.some((state) => !referencedStates.has(state.stateId))) throw new Error("history.states contains an unreferenced state");
}

export function parseLoomAuthoringHistory(value: unknown): LoomAuthoringHistory {
  const item = record(value, "history");
  exactKeys(item, ["schema", "initialHead", "states", "operations", "activeBranchOperationIds", "cursor", "activeHead"], "history");
  if (item.schema !== "loom-authoring-history/v1") throw new Error("history.schema must be loom-authoring-history/v1");
  if (!Array.isArray(item.states) || !Array.isArray(item.operations)) throw new Error("history states and operations must be arrays");
  const history: LoomAuthoringHistory = {
    schema: "loom-authoring-history/v1",
    initialHead: parseHead(item.initialHead, "history.initialHead"),
    states: item.states.map((state, index) => parseState(state, `history.states[${index}]`)),
    operations: item.operations.map((operation, index) => parseOperation(operation, `history.operations[${index}]`)),
    activeBranchOperationIds: uniqueStrings(item.activeBranchOperationIds, "history.activeBranchOperationIds"),
    cursor: integer(item.cursor, "history.cursor"),
    activeHead: parseHead(item.activeHead, "history.activeHead"),
  };
  if (history.cursor > history.activeBranchOperationIds.length) throw new Error("history.cursor exceeds active branch");
  validateReplay(history);
  return immutable(history);
}

export function createLoomAuthoringHistory(documentValue: unknown): LoomAuthoringHistory {
  const document = parseLoomAuthoringDocument(documentValue);
  const state = stateRecord(document);
  return parseLoomAuthoringHistory({
    schema: "loom-authoring-history/v1",
    initialHead: { stateId: state.stateId, revision: document.revision },
    states: [state],
    operations: [],
    activeBranchOperationIds: [],
    cursor: 0,
    activeHead: { stateId: state.stateId, revision: document.revision },
  });
}

export function loomAuthoringDocumentStateId(documentValue: unknown): string {
  return stateRecord(parseLoomAuthoringDocument(documentValue)).stateId;
}

export function currentLoomAuthoringDocument(historyValue: unknown): LoomAuthoringDocument {
  const history = parseLoomAuthoringHistory(historyValue);
  return immutable(documentAt(new Map(history.states.map((state) => [state.stateId, state])), history.activeHead));
}

export function previewLoomAuthoringCommand(historyValue: unknown, commandValue: unknown): LoomAuthoringDocument {
  const history = parseLoomAuthoringHistory(historyValue);
  return immutable(reduceLoomAuthoringCommand(currentLoomAuthoringDocument(history), parseLoomAuthoringCommand(commandValue)).document);
}

export function acceptLoomAuthoringCommand(
  historyValue: unknown,
  commandValue: unknown,
  acceptance: LoomCommandAcceptance = { origin: "api.command" },
): LoomAuthoringHistory {
  const history = parseLoomAuthoringHistory(historyValue);
  const command = parseLoomAuthoringCommand(commandValue);
  const legacyMirrorMutation = (command.kind === "node.add" && command.node.type === "transform" && command.node.operation.transform === "mirror-y")
    || (command.kind === "node.attach" && command.node.type === "transform" && command.node.operation.transform === "mirror-y")
    || (command.kind === "transform.replace" && command.operation.transform === "mirror-y");
  if (legacyMirrorMutation) throw new Error("new mirrored partners must use mirror.partner.create; mirror-y is replay-only");
  if (!(["api.command", "assistant.ollama", "gui.control", "gui.scene", "system.migration"] as const).includes(acceptance.origin)) throw new Error("operation origin is unsupported");
  if ((command.kind === "system.migration") !== (acceptance.origin === "system.migration")) throw new Error("system migrations require the system.migration origin and command");
  if (command.kind === "system.migration" && history.operations.some((operation) => operation.kind === "command.accepted" && operation.command.kind === "system.migration")) throw new Error("system migration is already audited");
  const subjectIds = uniqueStrings(acceptance.subjectIds ?? [], "acceptance.subjectIds");
  if (history.operations.some((operation) => operation.kind === "command.accepted" && operation.command.commandId === command.commandId)) throw new Error(`duplicate command id ${command.commandId}`);
  const reduction = reduceLoomAuthoringCommand(currentLoomAuthoringDocument(history), command);
  const resultDocument = reduction.document;
  const resultState = stateRecord(resultDocument);
  const existingState = history.states.find((state) => state.stateId === resultState.stateId);
  if (existingState && canonicalJson(existingState.definition) !== canonicalJson(resultState.definition)) throw new Error(`content hash collision at ${resultState.stateId}`);
  const sequence = history.operations.length;
  const discardedRedoOperationIds = history.activeBranchOperationIds.slice(history.cursor);
  const draft: LoomCommandAcceptedOperation = {
    schema: "loom-authoring-operation/v1",
    operationId: "pending",
    sequence,
    kind: "command.accepted",
    origin: acceptance.origin,
    subjectIds,
    extraction: reduction.extraction,
    parent: history.activeHead,
    result: { stateId: resultState.stateId, revision: resultDocument.revision },
    command,
    discardedRedoOperationIds,
  };
  const operation = sealOperation(draft);
  const activeBranchOperationIds = [...history.activeBranchOperationIds.slice(0, history.cursor), operation.operationId];
  return parseLoomAuthoringHistory({
    ...history,
    states: existingState ? history.states : [...history.states, resultState],
    operations: [...history.operations, operation],
    activeBranchOperationIds,
    cursor: activeBranchOperationIds.length,
    activeHead: operation.result,
  });
}

function moveHead(historyValue: unknown, move: "undo" | "redo"): LoomAuthoringHistory {
  const history = parseLoomAuthoringHistory(historyValue);
  const transitionId = move === "undo" ? history.activeBranchOperationIds[history.cursor - 1] : history.activeBranchOperationIds[history.cursor];
  if (!transitionId) throw new Error(`nothing to ${move}`);
  const transition = history.operations.find((operation): operation is LoomCommandAcceptedOperation => operation.kind === "command.accepted" && operation.operationId === transitionId);
  if (!transition) throw new Error(`${move} transition is missing`);
  const to = move === "undo" ? transition.parent : transition.result;
  const draft: LoomHeadMovedOperation = {
    schema: "loom-authoring-operation/v1",
    operationId: "pending",
    sequence: history.operations.length,
    kind: "head.moved",
    move,
    from: history.activeHead,
    to,
    transitionOperationId: transitionId,
  };
  const operation = sealOperation(draft);
  return parseLoomAuthoringHistory({
    ...history,
    operations: [...history.operations, operation],
    cursor: history.cursor + (move === "undo" ? -1 : 1),
    activeHead: to,
  });
}

export const undoLoomAuthoringHistory = (historyValue: unknown): LoomAuthoringHistory => moveHead(historyValue, "undo");
export const redoLoomAuthoringHistory = (historyValue: unknown): LoomAuthoringHistory => moveHead(historyValue, "redo");

export function deriveCurrentLoomDependentAncestry(historyValue: unknown): LoomDependentAncestry {
  const history = parseLoomAuthoringHistory(historyValue);
  const stateIds = [history.initialHead.stateId];
  const commandOperationIds: string[] = [];
  const commandById = new Map(history.operations
    .filter((operation): operation is LoomCommandAcceptedOperation => operation.kind === "command.accepted")
    .map((operation) => [operation.operationId, operation]));
  history.activeBranchOperationIds.slice(0, history.cursor).forEach((operationId) => {
    const operation = commandById.get(operationId)!;
    const priorIndex = stateIds.indexOf(operation.result.stateId);
    if (priorIndex >= 0) {
      stateIds.splice(priorIndex + 1);
      commandOperationIds.splice(priorIndex);
    } else {
      stateIds.push(operation.result.stateId);
      commandOperationIds.push(operationId);
    }
  });
  return immutable({ stateIds, commandOperationIds });
}

export function currentLoomExtractionDirty(historyValue: unknown): boolean {
  const history = parseLoomAuthoringHistory(historyValue);
  const dependent = new Set(deriveCurrentLoomDependentAncestry(history).commandOperationIds);
  return history.operations.some((operation) => (
    operation.kind === "command.accepted"
    && dependent.has(operation.operationId)
    && operation.extraction === "rebuild-required"
  ));
}
